/**
 * reserva-live-runtime.js — El asistente del bloque `reserva-live` (personas → día → hora).
 *
 * POR QUÉ ES UN MÓDULO Y NO on* INLINE (gotcha #13): el saneo del HTML publicado
 * (core_services/html_sanitizer.py) BORRA todos los atributos on*. Toda la interacción va por
 * DELEGACIÓN DE CLASE (`.rl-*`) con un puñado de listeners en `document`, igual que
 * core/block-actions.js —que es quien nos arranca— y que animations/form-submit.js.
 * Consecuencia buscada: re-sanear un sitio ya publicado NO lo rompe.
 *
 * CORRE EN LAS DOS SUPERFICIES (editor y publicado). En el canvas del editor
 * (`body.editor-active`) la disponibilidad SÍ se consulta —el dueño ve huecos reales con un
 * sello de "vista previa"— pero el ENVÍO queda INERTE, espejo exacto de form-submit.js.
 *
 * ══ CONTRATO DEL ENDPOINT (SSOT = el docstring de web/lib/booking_engine.py) ══
 *  Lo de abajo es cómo lo CONSUME este runtime. Los nombres que difieren entre las dos mitades
 *  se traducen en la frontera (ESTADO_SLOT / ESTADO_MOTIVO / `data.reserva`), nunca aguas
 *  adentro: el widget razona en su vocabulario y el motor en el suyo.
 *
 *  GET  /site_reserva/slots/?restaurant_id=&fecha=YYYY-MM-DD&personas=N&zona=<id>
 *    200 → { ok:true,
 *            turnos: [ { hora:'21:00', estado:'libre'|'escaso'|'lleno', quedan: 2|null } ],
 *            alternativas: [ { tipo:'hora'|'zona'|'dia', fecha?, hora?, zona?, zona_nombre? } ],
 *            motivo: ''|'cerrado'|'sin_hueco'|'fuera_de_antelacion'|'pax_fuera_de_rango',
 *            politica: {…} }
 *    · `estado` de cada hueco: 'libre'/'escaso' ⇒ pulsable, 'lleno' ⇒ deshabilitado.
 *    · las alternativas viajan como DATOS (sin frase): la etiqueta la compone el widget en el
 *      idioma de la página (`etiquetaAlternativa`), nunca el servidor.
 *    · `alternativas` viene SIEMPRE, aunque `turnos` esté vacío: "no hay mesa" nunca puede ser
 *      un callejón sin salida (misma noche ±30 min, otra zona, día siguiente).
 *    · `quedan` es OPCIONAL y solo se pinta si es un número: escasez HONESTA. Sin dato, el
 *      bloque se CALLA (mismo principio ético que hizo `reviews` manualOnly). Su UNIDAD son
 *      **MESAS donde ese grupo cabe**, no plazas sueltas: el motor lleva inventario por mesa
 *      (`ReservationTableSlot`), y por eso las etiquetas `rl_left*` dicen "mesas" en los 9
 *      idiomas. Publicar plazas repartidas por la sala era prometer un sitio que no existía.
 *    · Se acepta `slots` como alias de `turnos` (el documento de origen usa las dos palabras);
 *      tolerancia deliberada, no invención de contrato.
 *
 *  POST /site_reserva/confirmar/   (FormData)
 *    restaurant_id, fecha, hora, personas, zona,
 *    nombre|telefono|email|notas|alergias  ← ROLES SEMÁNTICOS (data-field-role), NUNCA la
 *    etiqueta visible: `translatable.js` la traduce a 9 idiomas y una reserva identificada por
 *    "Teléfono" se rompería el día que alguien publica en catalán. `alergias` va aparte de
 *    `notas` porque es su propia columna y DATO DE SALUD (no sale por correo).
 *    trona|movilidad ('1' si el visitante marcó la casilla; solo se pintan si la política las pide)
 *    _idem (clave de idempotencia PERSISTIDA en localStorage: al recargar se reutiliza y el
 *    servidor devuelve la misma reserva en vez de crear otra), _hp (honeypot), page_url, _lang
 *    200 → { ok:true, reserva:{ codigo, estado, fecha, hora, personas, zona, zona_nombre,
 *                               cancel_token } }
 *        → { ok:false, error:'module_off'|'sin_hueco'|'limite_telefono'|… }  (409/400/429)
 *
 *  POST /site_reserva/cancelar/    (FormData: t=<cancel_token>)
 *    La MISMA puerta que el enlace del correo. El GET de esa ruta solo PREGUNTA (los correos los
 *    abren máquinas); cancelar es siempre POST. 200 → { ok:true, estado:'cancelada'|'ya' }.
 *
 *  CUALQUIER problema (timeout, red, 5xx, `error:'module_off'`, JSON ilegible) ⇒ DEGRADACIÓN
 *  al modo de hoy: POST a /site_lead/ y "te confirmamos en breve". El cliente NUNCA pierde una
 *  reserva por un fallo nuestro. El presupuesto para PINTAR algo son 800 ms (`SLOTS_PAINT_MS`),
 *  que NO es el mismo reloj que el de rendirse (`SLOTS_TIMEOUT_MS`): un servidor lento pinta el
 *  esqueleto horneado y luego se corrige solo — degradar es el último recurso, no el primero.
 *
 *  Nota de rutas: las URLs son RAÍZ-relativas, mismo criterio que animations/form-submit.js
 *  con `/site_lead/` (el sitio publicado se sirve por subdominio del tenant). En el editor —que
 *  vive bajo /{tenant}/builder/— la consulta puede fallar: eso ya es el camino degradado, y por
 *  eso el sello dice "vista previa".
 */
import { t, tv, tvn, getRenderLang } from '../utils/i18n.js';
import { escapeHtml } from '../utils/dom.js';
import { leerDeepLink } from '../utils/deep-link.js';
// Prefijo de las rutas públicas (SSOT, compartido con animations/form-submit.js).
import { basePublica } from '../utils/base-publica.js';

export const RESERVA_ENDPOINTS = {
    slots: '/site_reserva/slots/',
    confirmar: '/site_reserva/confirmar/',
    cancelar: '/site_reserva/cancelar/',
    lead: '/site_lead/',
};

// DOS RELOJES PARA LOS HUECOS, y la diferencia entre ellos es dinero del cliente:
//   · SLOTS_PAINT_MS   — presupuesto para PINTAR algo (el bloque no puede quedarse "cargando"),
//                        tras el cual se enseña el esqueleto horneado como PROVISIONAL.
//   · SLOTS_TIMEOUT_MS — cuándo se ABANDONA de verdad la petición.
// Eran el MISMO número (800 ms) y ahí estaba el fallo: un servidor que tardaba 900 ms dejaba el
// widget `degradado` para siempre, y `confirmar()` manda las degradadas a /site_lead/ → la reserva
// del visitante acababa en el buzón de contactos en vez de en la agenda, sin que el local ni el
// comensal se enteraran de que se había perdido una mesa. Pintar rápido y rendirse rápido no son
// la misma decisión: ahora se pinta a los 800 ms y se sigue esperando la verdad hasta 6 s.
// El POST de confirmación va aparte (abortarlo a 800 ms crearía reservas fantasma).
const SLOTS_PAINT_MS = 800;
const SLOTS_TIMEOUT_MS = 6000;
const CONFIRM_TIMEOUT_MS = 8000;

const STEPS = ['people', 'day', 'time', 'details', 'done'];

// ── VOCABULARIO DEL SERVIDOR → VOCABULARIO DEL WIDGET ────────────────────────
// El motor publica los estados de un hueco como 'libre'|'escaso'|'lleno'
// (web/lib/booking_engine.py: ESTADO_LIBRE/ESTADO_ESCASO/ESTADO_LLENO) y el widget razona en
// 'ok'|'waitlist'|'full'. Traducirlos aquí, en la frontera, es obligatorio: sin esta tabla un
// hueco 'lleno' caía al default 'ok' y se pintaba PULSABLE (el visitante elegía una hora que el
// servidor rechaza después), y a la vez ningún turno contaba como 'ok', así que el bloque
// enseñaba SIEMPRE el copy de "no hay mesa" aunque quedaran huecos. Se aceptan también los
// nombres del widget para que un servidor que ya hable su idioma siga funcionando.
const ESTADO_SLOT = {
    libre: 'ok', escaso: 'ok', ok: 'ok',
    lleno: 'full', full: 'full',
    waitlist: 'waitlist', espera: 'waitlist',
};

// `motivo` del contrato (booking_engine.py) → estado del widget. Solo se traduce lo que NO se
// puede deducir de los huecos: un día cerrado no tiene turnos, así que sin esto se contaría como
// "lleno" y el cliente vería el copy equivocado. El resto de motivos se deja al cálculo por
// huecos (sin turnos libres ⇒ 'full'), que es lo que el visitante entiende.
const ESTADO_MOTIVO = { cerrado: 'closed' };

const estadoDeSlot = (v) => ESTADO_SLOT[String(v || '').toLowerCase()] || 'ok';

// Estado por widget. WeakMap (no atributos en el DOM): al re-renderizar el editor el elemento
// es NUEVO → estado limpio sin necesidad de invalidar nada.
const _estado = new WeakMap();

// ── Utilidades de fecha (sin dependencias; todo en hora LOCAL del visitante) ──
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const hm = (s) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(String(s || '').trim());
    return m ? [parseInt(m[1], 10), parseInt(m[2], 10)] : null;
};

/** Etiqueta larga de una fecha en el idioma de render (sin claves de traducción propias). */
function fechaLarga(d) {
    try {
        return d.toLocaleDateString(getRenderLang(), { weekday: 'long', day: 'numeric', month: 'long' });
    } catch (_) { return ymd(d); }
}

/**
 * Días con servicio de la sala elegida (convención `Date.getDay()`), o `null` si no hay sala
 * elegida o no se sabe — y entonces manda la política del local, como siempre.
 *
 * El dato VIVO (`politica.zonas[].dias_js`, que llega con cada consulta de huecos) PISA al horneado
 * (`data-live-params.zones[].days`): cambiar los turnos en el ERP se nota en la web publicada sin
 * volver a publicarla, igual que los interruptores de trona y movilidad. El horneado es lo que
 * permite tachar los días a 0 ms, antes de la primera petición.
 */
export function diasDeSala(st) {
    if (!st || !st.zona) { return null; }
    const vivas = (st.politica && Array.isArray(st.politica.zonas)) ? st.politica.zonas : null;
    if (vivas) {
        const z = vivas.find((v) => String(v.slug || '') === String(st.zona));
        if (z && Array.isArray(z.dias_js)) { return z.dias_js; }
    }
    const horneada = (st.zonas || []).find((v) => String(v.id || '') === String(st.zona));
    return (horneada && Array.isArray(horneada.days)) ? horneada.days : null;
}

/**
 * ¿La política descarta este día? (cierre semanal, festivo, antelación, y los días en los que la
 * SALA elegida no tiene servicio).
 *
 * `diasSala` es la lista de días con servicio de la sala elegida, en la convención de
 * `Date.getDay()` (0=domingo). Sin ella —o sin sala elegida— manda solo la política del local, que
 * es el comportamiento de siempre. Con ella, un reservado que solo abre los domingos deja de tener
 * toda la semana pulsable: antes el visitante tenía que pulsar un día para que el servidor le
 * dijera "no nos queda mesa", y adivinar cuál probar.
 */
export function diaBloqueado(cfg, fecha, ahora, diasSala) {
    const hoy = startOfDay(ahora);
    const dia = startOfDay(fecha);
    const minDia = startOfDay(new Date(ahora.getTime() + (Number(cfg.min_hours) || 0) * 3600000));
    if (dia < minDia) return 'pasado';
    if (dia > addDays(hoy, Number(cfg.max_days) || 60)) return 'lejano';
    if ((cfg.closed_weekdays || []).map(Number).indexOf(dia.getDay()) !== -1) return 'cerrado';
    if ((cfg.closed_dates || []).indexOf(ymd(dia)) !== -1) return 'cerrado';
    // Lista VACÍA = esa sala no tiene ningún turno: no se tacha el mes entero (sería una pantalla
    // muerta sin explicación); se deja que el endpoint lo diga con su copy de "cerrado".
    if (Array.isArray(diasSala) && diasSala.length
        && diasSala.map(Number).indexOf(dia.getDay()) === -1) { return 'cerrado'; }
    return '';
}

/** Turnos derivados de la POLÍTICA horneada (sin red). Es lo que se pinta a 0 ms y también el
 *  esqueleto al que se cae si el servidor no responde: horas SIN "quedan" (no inventamos dato). */
export function turnosHorneados(cfg, fecha, ahora) {
    const out = [];
    const limite = new Date(ahora.getTime() + (Number(cfg.min_hours) || 0) * 3600000);
    (cfg.shifts || []).forEach((s) => {
        const a = hm(s.from); const b = hm(s.to);
        if (!a || !b) return;
        const paso = Math.max(5, Math.min(120, Number(s.step) || 30));
        let cur = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate(), a[0], a[1]);
        const fin = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate(), b[0], b[1]);
        let guarda = 0;
        while (cur <= fin && guarda++ < 96) {
            if (cur >= limite) {
                out.push({ hora: `${String(cur.getHours()).padStart(2, '0')}:${String(cur.getMinutes()).padStart(2, '0')}`, estado: 'ok', quedan: null });
            }
            cur = new Date(cur.getTime() + paso * 60000);
        }
    });
    return out;
}

/** uuid v4 de cliente (crypto si existe). Viaja como `_idem`: el doble toque no reserva dos veces. */
function nuevoIdem() {
    try {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    } catch (_) { /* navegador antiguo */ }
    return 'idem-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

// ── LA RESERVA SE RECUERDA AL RECARGAR ───────────────────────────────────────
//
// EL PROBLEMA: la clave de idempotencia nacía en memoria (un WeakMap por widget). Al recargar la
// página nacía OTRA, así que el servidor veía una petición distinta y creaba una segunda reserva.
// El `unique_together(restaurant, idempotency_key)` solo cubría el doble clic dentro de la misma
// página, que es el caso fácil.
//
// LA SOLUCIÓN, en dos capas y en este orden de importancia:
//   1. **Servidor (la de verdad):** la clave se PERSISTE y se reutiliza. Si el visitante vuelve a
//      enviar el formulario después de recargar, viaja el MISMO `_idem` → `crear_reserva` devuelve
//      la reserva que ya existía en vez de crear otra. No depende de que el navegador colabore.
//   2. **Interfaz:** al cargar, el widget arranca en el paso de confirmación con SU reserva, así
//      que el visitante no vuelve a rellenar nada (y no lo intenta).
//
// QUÉ SE GUARDA: código, día/hora/personas/zona, la clave de idempotencia y el token de anulación.
// **NUNCA nombre, teléfono, email, alergias ni notas.** No hacen falta para repintar el panel y
// `localStorage` es un almacén que sobrevive al visitante, se comparte entre pestañas y lo lee
// cualquier script de la página: los datos personales no viven ahí.
const LS_RESERVA = 'n3i_rl_reserva_';
const LS_CUENTA = 'n3i_rl_cuenta_';
//: La clave de idempotencia se persiste ANTES de enviar, no al recibir el "ok". Es la diferencia
//: entre una clave de idempotencia y un identificador decorativo: el caso que de verdad duplica
//: reservas es el envío cuya RESPUESTA se pierde (móvil en un túnel, servidor lento). Ahí el
//: visitante recarga y vuelve a enviar sin saber que su reserva SÍ se creó; con la clave
//: guardada de antemano, el servidor reconoce la petición y devuelve la misma reserva.
const LS_IDEM = 'n3i_rl_idem_';
//: Cuánto vale esa clave en vuelo. Sin caducidad se volvería un zombi: alguien que abandonó el
//: formulario hace meses reenviaría con la clave de entonces y el servidor le devolvería como
//: "confirmada" una reserva del pasado. Unas horas cubren el reintento honesto y nada más.
const TTL_IDEM_MS = 6 * 3600 * 1000;
//: Tope de reservas por navegador. Es **FRICCIÓN, NO UN MURO**: se salta con una ventana de
//: incógnito o borrando datos del sitio, y así debe entenderse. El freno real contra el abuso es
//: `max_reservas_telefono_dia`, que vive en el SERVIDOR (política del restaurante) y que ningún
//: navegador puede esquivar. Esto solo evita el descuido honesto de reservar tres veces seguidas.
const MAX_POR_NAVEGADOR = 2;
//: Ventana del tope. Sin ella el contador no decaería nunca y una familia que reserva cada semana
//: se quedaría bloqueada para siempre en ese navegador — un fallo mucho peor que el que evita.
const VENTANA_TOPE_MS = 24 * 3600 * 1000;
//: Margen tras la hora reservada antes de olvidar la reserva guardada. Cubre la sobremesa: el
//: panel sigue enseñando el código mientras el comensal está en la mesa, y desaparece solo.
const MARGEN_OLVIDO_MS = 4 * 3600 * 1000;

// localStorage puede LANZAR (Safari en privado, cookies bloqueadas, cuota llena). Todo el acceso
// pasa por estos tres envoltorios: sin almacén, el widget se comporta exactamente como antes.
function lsLeer(clave) {
    try { return window.localStorage.getItem(clave); } catch (_) { return null; }
}
function lsEscribir(clave, valor) {
    try { window.localStorage.setItem(clave, valor); } catch (_) { /* sin almacén: seguimos */ }
}
function lsBorrar(clave) {
    try { window.localStorage.removeItem(clave); } catch (_) { /* idem */ }
}

/** Instante (ms) en el que una reserva deja de recordarse: su hora + la sobremesa. */
function caducidadDe(fecha, hora) {
    const f = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(fecha || ''));
    const h = hm(hora);
    if (!f) return Date.now() + 24 * 3600 * 1000;    // sin fecha legible: un día y se olvida
    const d = new Date(Number(f[1]), Number(f[2]) - 1, Number(f[3]), h ? h[0] : 23, h ? h[1] : 59);
    return d.getTime() + MARGEN_OLVIDO_MS;
}

/** La reserva recordada de ese restaurante, o null (y de paso limpia la caducada). */
export function reservaGuardada(restaurantId) {
    if (!restaurantId) return null;
    const crudo = lsLeer(LS_RESERVA + restaurantId);
    if (!crudo) return null;
    let datos = null;
    try { datos = JSON.parse(crudo); } catch (_) { datos = null; }
    if (!datos || typeof datos !== 'object') { lsBorrar(LS_RESERVA + restaurantId); return null; }
    if (!datos.expira || Number(datos.expira) < Date.now()) {
        lsBorrar(LS_RESERVA + restaurantId);
        return null;
    }
    return datos;
}

/** Recuerda la reserva recién hecha. `reserva` es lo que devolvió el endpoint (incluido su
 *  `cancel_token`, la llave firmada del botón «Anular»). Devuelve lo guardado, o null. */
function guardarReserva(restaurantId, st, reserva) {
    if (!restaurantId) return null;
    const fecha = reserva.fecha || (st.fecha ? ymd(st.fecha) : '');
    const hora = reserva.hora || (st.turno ? st.turno.hora : '');
    const datos = {
        codigo: reserva.codigo || '',
        fecha,
        hora,
        personas: Number(reserva.personas || st.personas) || st.personas,
        zona: reserva.zona || st.zona || '',
        zona_nombre: reserva.zona_nombre
            || (st.zonas.find((z) => String(z.id) === String(st.zona)) || {}).name || '',
        idem: st.idem,
        cancel: reserva.cancel_token || '',
        expira: caducidadDe(fecha, hora),
    };
    lsEscribir(LS_RESERVA + restaurantId, JSON.stringify(datos));
    return datos;
}

/** Clave de idempotencia en vuelo de este navegador para ese restaurante ('' si no hay o caducó). */
function idemEnVuelo(restaurantId) {
    if (!restaurantId) return '';
    let datos = null;
    try { datos = JSON.parse(lsLeer(LS_IDEM + restaurantId) || 'null'); } catch (_) { datos = null; }
    if (!datos || !datos.v || Number(datos.ts || 0) + TTL_IDEM_MS < Date.now()) {
        if (datos) lsBorrar(LS_IDEM + restaurantId);
        return '';
    }
    return String(datos.v);
}

function recordarIdem(restaurantId, idem) {
    if (!restaurantId || !idem) return;
    lsEscribir(LS_IDEM + restaurantId, JSON.stringify({ v: idem, ts: Date.now() }));
}

/** Marcas de tiempo de las reservas hechas en la ventana del tope (las viejas se descartan). */
function reservasRecientes(restaurantId) {
    if (!restaurantId) return [];
    let lista = [];
    try { lista = JSON.parse(lsLeer(LS_CUENTA + restaurantId) || '[]'); } catch (_) { lista = []; }
    if (!Array.isArray(lista)) return [];
    const desde = Date.now() - VENTANA_TOPE_MS;
    return lista.map(Number).filter((t) => t > desde);
}

function anotarReserva(restaurantId) {
    if (!restaurantId) return;
    const lista = reservasRecientes(restaurantId).concat([Date.now()]);
    lsEscribir(LS_CUENTA + restaurantId, JSON.stringify(lista.slice(-10)));
}

/** Al anular se devuelve el hueco del tope: quien cancela no ha "gastado" una reserva. */
function descontarReserva(restaurantId) {
    if (!restaurantId) return;
    const lista = reservasRecientes(restaurantId);
    lista.pop();
    lsEscribir(LS_CUENTA + restaurantId, JSON.stringify(lista));
}

function topeAlcanzado(st) {
    return !!st.restaurantId && reservasRecientes(st.restaurantId).length >= MAX_POR_NAVEGADOR;
}

function esEditor() {
    return typeof document !== 'undefined' && document.body.classList.contains('editor-active');
}

// ── Estado del widget ────────────────────────────────────────────────────────
function leerJson(el, attr) {
    try { return JSON.parse(el.getAttribute(attr) || '{}'); } catch (_) { return {}; }
}

function estadoDe(widget) {
    let st = _estado.get(widget);
    if (st) return st;
    const section = widget.closest('section') || widget.parentElement;
    const cfg = section ? leerJson(section, 'data-booking-config') : {};
    const params = section ? leerJson(section, 'data-live-params') : {};
    const zonas = Array.isArray(params.zones) ? params.zones : [];
    const restaurantId = params.restaurant_id || '';
    // En el canvas del editor NO se restaura nada: el dueño estaría viendo la reserva que hizo
    // probando su propia web en vez de su bloque. El envío ya es inerte ahí (esEditor()).
    const recordada = esEditor() ? null : reservaGuardada(restaurantId);
    st = {
        cfg,
        restaurantId,
        modo: widget.getAttribute('data-mode') === 'live' ? 'live' : 'lead',
        zonas,
        // Las etiquetas HORNEADAS al publicar, por id. Son las únicas TRADUCIDAS: viajan en los
        // props (`source.zones[].name`), que sí pasan por el traductor, mientras que las que llegan
        // del endpoint traen el nombre tal cual está en el ERP —uno solo, sin idiomas—. Se guardan
        // aparte porque `sincronizarZonas` reescribe `st.zonas` y si no se perderían al primer
        // repintado. Ver `sincronizarZonas`.
        etiquetasHorneadas: zonas.reduce((acc, z) => {
            if (z && z.id != null && typeof z.name === 'string' && z.name.trim()) {
                acc[String(z.id)] = z.name;
            }
            return acc;
        }, {}),
        zona: zonas.length ? String(zonas[0].id) : '',
        personas: Number(cfg.party_default) || 2,
        fecha: null,
        mes: startOfDay(new Date()),
        turno: null,
        turnos: [],
        alternativas: [],
        // Hora que el visitante PREFIERE, cuando la sabemos: llega en el deep-link o es la que
        // acaba de evaporarse al confirmar. Viaja como `&hora=` a `/site_reserva/slots/`, que es lo
        // ÚNICO que hace al motor emitir alternativas de tipo 'hora' (`booking_engine.disponibilidad`
        // solo las añade `if hora_preferida`) → sin esto, el "misma noche, ±30 min" que el bloque
        // sabe pintar no salía NUNCA en la web; solo lo veía el maître desde el panel.
        horaPref: '',
        // Nº de la carga de huecos en curso: solo la ÚLTIMA puede escribir en este estado (ver
        // `cargarTurnos`). Por widget, como todo lo demás de aquí.
        gen: 0,
        degradado: false,      // el servidor no contestó → el envío irá a /site_lead/
        // Política VIVA del restaurante (llega con los huecos). De aquí salen las casillas de
        // trona / acceso sin escaleras: nada de esto está horneado en el sitio publicado.
        politica: {},
        // LA CLAVE SE HEREDA: de la reserva ya confirmada, o de la que quedó EN VUELO (envío cuya
        // respuesta no llegó). Reenviar tras recargar tiene que devolver la MISMA reserva, no
        // crear una segunda — ver el bloque de persistencia de arriba.
        idem: (recordada && recordada.idem)
            || (esEditor() ? '' : idemEnVuelo(restaurantId))
            || nuevoIdem(),
        recordada,
        anulada: false,
        paso: 'people',
        enviando: false,
    };
    _estado.set(widget, st);
    return st;
}

// ── Pintado ──────────────────────────────────────────────────────────────────
/**
 * Escribe en la región viva del widget. `urgente` marca los avisos que FRENAN al visitante
 * (hoy, el tope por dispositivo): se pintan resaltados y sacuden el formulario.
 *
 * Por qué la sacudida y no solo el texto: el mensaje aparece DEBAJO del botón que se acaba de
 * pulsar, y quien pulsa está mirando el botón. Sin movimiento, el aviso se escribe fuera de donde
 * está la vista y parece que el botón no ha hecho nada — así que se vuelve a pulsar.
 *
 * `role="status"`/`aria-live="polite"` NO se tocan: quien usa lector de pantalla ya recibe el texto
 * anunciado, y subirlo a `assertive` interrumpiría también a los avisos normales que comparten esta
 * misma región. El resalte es para quien VE la pantalla; el anuncio ya funcionaba.
 */
function anunciar(widget, msg, modo) {
    const live = widget.querySelector('.rl-live');
    if (live) {
        live.textContent = msg || '';
        live.classList.toggle('rl-live-alerta', !!(msg && modo));
    }
    // Resaltar sí, sacudir solo cuando el visitante acaba de INTENTARLO: llegar al paso y que la
    // pantalla tiemble sola, sin haber tocado nada, es ruido.
    if (!msg || modo !== 'sacudir') return;
    const caja = widget.querySelector('.rl-form') || widget;
    // Quitar y forzar un reflow antes de volver a poner la clase: si no, pulsar dos veces seguidas
    // no repite la animación (para el navegador la clase nunca cambió) y el segundo intento se
    // quedaría sin respuesta visible, que es justo lo que este aviso viene a arreglar.
    caja.classList.remove('rl-sacude');
    void caja.offsetWidth;
    caja.classList.add('rl-sacude');
}

/**
 * Muestra el copy EDITABLE del estado que toca y esconde los otros cuatro.
 * En el CANVAS DEL EDITOR los enseña TODOS: el cliente tiene que poder reescribir el "no hay
 * mesa" sin llenar el restaurante para provocarlo. En el sitio servido manda el ENDPOINT.
 */
function mostrarEstado(widget, clave) {
    const editor = esEditor();
    widget.querySelectorAll('.rl-state-copy').forEach((el) => {
        el.hidden = editor ? false : (el.getAttribute('data-state-copy') !== clave);
    });
}

/**
 * `opts.silencioso` = no robar el foco al llegar a la confirmación. Se usa al RESTAURAR una
 * reserva recordada: el visitante acaba de cargar la página y no ha pulsado nada, así que
 * enfocar el panel (y arrastrar el scroll hasta el bloque) sería moverle la página sin motivo.
 */
function setPaso(widget, st, paso, opts) {
    st.paso = STEPS.indexOf(paso) === -1 ? 'people' : paso;
    widget.querySelectorAll('.rl-panel').forEach((el) => {
        el.hidden = el.getAttribute('data-step') !== st.paso;
    });
    widget.querySelectorAll('.rl-step').forEach((el) => {
        const k = el.getAttribute('data-step-pill');
        el.classList.toggle('is-active', k === st.paso);
        el.classList.toggle('is-done', STEPS.indexOf(k) < STEPS.indexOf(st.paso));
    });
    const back = widget.querySelector('.rl-back');
    const next = widget.querySelector('.rl-next');
    if (back) back.hidden = (st.paso === 'people' || st.paso === 'done');
    if (next) next.hidden = (st.paso !== 'people' && st.paso !== 'day');
    // Al ENTRAR en el paso de datos se resuelve el consentimiento de alergias por si el campo ya
    // viene relleno: el autocompletado del navegador no dispara `input`, así que sin esto la
    // casilla no aparecería y el envío se bloquearía sin nada visible que marcar.
    if (st.paso === 'details') pintarConsentimiento(widget);
    if (st.paso === 'done' && !(opts && opts.silencioso)) {
        const done = widget.querySelector('.rl-done');
        // Foco a la confirmación: es el final del flujo y quien navega con teclado tiene que
        // aterrizar ahí (tabindex="-1" en el panel, no robamos foco a nadie más).
        if (done && typeof done.focus === 'function') done.focus();
    }
}

function pintarPersonas(widget, st) {
    const cont = widget.querySelector('.rl-people');
    if (!cont) return;
    const opciones = (st.cfg.party_options || []).map(Number).filter((n) => n > 0);
    const min = Number(st.cfg.party_min) || 1;
    const max = Number(st.cfg.party_max) || 12;
    cont.innerHTML = opciones.map((n) => {
        const fuera = n < min || n > max;
        const activo = n === st.personas;
        return `<button type="button" class="rl-chip rl-person${activo ? ' is-active' : ''}" data-people="${n}" role="radio" aria-checked="${activo ? 'true' : 'false'}"${fuera ? ' disabled aria-disabled="true"' : ''}>${n}</button>`;
    }).join('') + `<button type="button" class="rl-chip rl-person rl-person-more" data-people="more">${escapeHtml(String(max))}+</button>`;
}

function pintarCalendario(widget, st) {
    const cont = widget.querySelector('.rl-cal');
    const rotulo = widget.querySelector('.rl-cal-month');
    if (!cont) return;
    const ahora = new Date();
    const mes = st.mes;
    if (rotulo) {
        try { rotulo.textContent = mes.toLocaleDateString(getRenderLang(), { month: 'long', year: 'numeric' }); }
        catch (_) { rotulo.textContent = `${mes.getMonth() + 1}/${mes.getFullYear()}`; }
    }

    // Cabecera de días: nombres cortos del propio Intl (sin claves de traducción nuevas).
    // Semana que empieza en LUNES (norma europea); getDay() da 0=domingo → se rota.
    const cabecera = [];
    for (let i = 0; i < 7; i++) {
        const ref = new Date(2024, 0, 1 + i);           // 2024-01-01 fue lunes
        let nombre = String(i + 1);
        try { nombre = ref.toLocaleDateString(getRenderLang(), { weekday: 'short' }); } catch (_) { /* Intl ausente */ }
        // aria-hidden: cada botón de día ya lleva su fecha COMPLETA en aria-label
        // ("miércoles, 29 de julio"), así que la fila de iniciales sería ruido para el lector.
        cabecera.push(`<span class="rl-cal-dow" aria-hidden="true">${escapeHtml(nombre)}</span>`);
    }

    const primero = new Date(mes.getFullYear(), mes.getMonth(), 1);
    const hueco = (primero.getDay() + 6) % 7;           // lunes = 0
    const diasMes = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).getDate();
    const celdas = [];
    for (let i = 0; i < hueco; i++) celdas.push('<span class="rl-day rl-day-void" aria-hidden="true"></span>');
    for (let d = 1; d <= diasMes; d++) {
        const fecha = new Date(mes.getFullYear(), mes.getMonth(), d);
        const motivo = diaBloqueado(st.cfg, fecha, ahora, diasDeSala(st));
        const sel = st.fecha && ymd(st.fecha) === ymd(fecha);
        celdas.push(`<button type="button" class="rl-day${motivo ? ' is-off' : ''}${sel ? ' is-active' : ''}" data-day="${ymd(fecha)}"${motivo ? ' disabled aria-disabled="true"' : ''} aria-label="${escapeHtml(fechaLarga(fecha))}"${sel ? ' aria-current="date"' : ''}>${d}</button>`);
    }

    const minDia = startOfDay(new Date(ahora.getTime() + (Number(st.cfg.min_hours) || 0) * 3600000));
    const maxDia = addDays(startOfDay(ahora), Number(st.cfg.max_days) || 60);
    // Escape hatch de accesibilidad: si el calendario a medida no le sirve a alguien (lector de
    // pantalla exótico, teclado raro), el input NATIVO de fecha ya es accesible por definición y
    // alimenta el MISMO estado. La regla del plan es literal: si el picker no pasa la prueba de
    // teclado, hay que poder caer al nativo.
    const nativo = `<label class="rl-date-native-wrap"><span class="rl-date-native-label">${escapeHtml(t('rl_pick_day'))}</span>
        <input type="date" class="rl-date-native" min="${ymd(minDia)}" max="${ymd(maxDia)}"${st.fecha ? ` value="${ymd(st.fecha)}"` : ''}></label>`;

    cont.innerHTML = `<div class="rl-cal-grid">${cabecera.join('')}${celdas.join('')}</div>${nativo}`;
}

/**
 * Texto de una alternativa. El servidor manda los DATOS ({tipo, fecha, hora, zona, zona_nombre})
 * y no una frase — es lo correcto: el copy no puede venir hardcodeado del backend ni en un solo
 * idioma. Aquí se compone con lo que HAY, y solo si no hay nada se cae al rótulo genérico (que
 * sin esto salía duplicado: "Otra hora / Otra hora", sin decir qué otra hora).
 */
function etiquetaAlternativa(a, tipo) {
    if (a.label) return String(a.label);
    const partes = [];
    if (tipo === 'zona' && (a.zona_nombre || a.zona)) partes.push(String(a.zona_nombre || a.zona));
    if (tipo === 'dia' && a.fecha) {
        const p = String(a.fecha).split('-').map(Number);
        partes.push(p.length === 3 && p[0] ? fechaLarga(new Date(p[0], p[1] - 1, p[2])) : String(a.fecha));
    }
    if (a.hora) partes.push(String(a.hora));
    return partes.join(' · ');
}

function pintarTurnos(widget, st, opts) {
    const cont = widget.querySelector('.rl-slots');
    const alts = widget.querySelector('.rl-alts');
    if (!cont) return;
    const cargando = opts && opts.cargando;
    const dia = widget.querySelector('.rl-picked-day');
    if (dia) dia.textContent = st.fecha ? fechaLarga(st.fecha) : '';

    if (cargando) {
        cont.innerHTML = `<p class="rl-loading">${escapeHtml(t('rl_loading'))}</p>`;
        if (alts) { alts.hidden = true; alts.innerHTML = ''; }
        return;
    }

    if (!st.turnos.length) {
        cont.innerHTML = '';
    } else {
        cont.innerHTML = st.turnos.map((s) => {
            const estado = ['ok', 'waitlist', 'full'].indexOf(s.estado) !== -1 ? s.estado : 'ok';
            const activo = st.turno && st.turno.hora === s.hora;
            // ESCASEZ HONESTA: el badge SOLO si el servidor mandó un número. Sin dato, silencio.
            // El número son ACOMODOS —grupos de ESE tamaño que aún caben—, no plazas sueltas.
            //
            // Y cuando el acomodo son VARIAS MESAS JUNTAS se dice, en vez de "Queda 1 mesa": con
            // ocho comensales y tres mesas de cuatro, `quedan` es 1 pero lo que te van a poner son
            // DOS mesas juntas, y eso el comensal quiere saberlo ANTES de reservar, no al llegar.
            // `mesas_juntas` es aditivo: un servidor viejo no lo manda y se pinta como siempre.
            const combina = typeof s.mesas_juntas === 'number' && s.mesas_juntas > 1;
            const juntas = combina
                ? `<span class="rl-slot-join">${escapeHtml(tv('rl_juntas', { n: s.mesas_juntas }))}</span>` : '';
            // Cuando se juntan mesas, la escasez NO puede medirse en "mesas": el hueco decía
            // "Juntamos 2 mesas · Queda 1 mesa" — dos cifras de mesas contradiciéndose en la misma
            // etiqueta. Ahí el número son HUECOS (acomodos para ese grupo), y así se dice.
            const clave = combina ? 'rl_left_huecos' : 'rl_left';
            const quedan = (typeof s.quedan === 'number' && s.quedan > 0 && s.quedan <= 5)
                ? `<span class="rl-slot-left">${escapeHtml(tvn(clave, { n: s.quedan }))}</span>` : '';
            const etiqueta = estado === 'waitlist' ? t('rl_waitlist') : (estado === 'full' ? t('rl_no_slots') : '');
            return `<button type="button" class="rl-slot rl-slot-${estado}${activo ? ' is-active' : ''}" data-slot="${escapeHtml(s.hora)}" data-slot-state="${estado}"${estado === 'full' ? ' disabled aria-disabled="true"' : ''} aria-label="${escapeHtml(s.hora + (etiqueta ? ' · ' + etiqueta : ''))}">
                <span class="rl-slot-h">${escapeHtml(s.hora)}</span>${juntas}${quedan}</button>`;
        }).join('');
    }

    // "No hay mesa" NUNCA es un callejón sin salida: se pintan las salidas que dé el servidor.
    if (alts) {
        const lista = Array.isArray(st.alternativas) ? st.alternativas.slice(0, 3) : [];
        alts.hidden = lista.length === 0;
        alts.innerHTML = lista.map((a, i) => {
            const tipo = ['hora', 'zona', 'dia'].indexOf(a.tipo) !== -1 ? a.tipo : 'hora';
            const rotulo = etiquetaAlternativa(a, tipo)
                || t(tipo === 'zona' ? 'rl_alt_zone' : (tipo === 'dia' ? 'rl_alt_day' : 'rl_alt_time'));
            return `<button type="button" class="rl-alt rl-alt-${tipo}" data-alt="${i}">
                <span class="rl-alt-kind">${escapeHtml(t(tipo === 'zona' ? 'rl_alt_zone' : (tipo === 'dia' ? 'rl_alt_day' : 'rl_alt_time')))}</span>
                <span class="rl-alt-label">${escapeHtml(rotulo)}</span></button>`;
        }).join('');
    }
}

function pintarResumen(widget, st) {
    const cont = widget.querySelector('.rl-summary');
    if (!cont) return;
    const zona = (st.zonas.find((z) => String(z.id) === String(st.zona)) || {}).name || '';
    const filas = [
        [t('rl_people'), String(st.personas)],
        [t('rl_pick_day'), st.fecha ? fechaLarga(st.fecha) : ''],
        [t('rl_pick_time'), st.turno ? st.turno.hora : ''],
    ];
    if (zona) filas.push([t('rl_zone'), zona]);
    cont.innerHTML = filas.filter((f) => f[1]).map((f) => `
        <span class="rl-sum-item"><span class="rl-sum-k">${escapeHtml(f[0])}</span><span class="rl-sum-v">${escapeHtml(f[1])}</span></span>`).join('');
}

/**
 * Casillas EXTRA del paso de datos: trona y acceso sin escaleras.
 *
 * Se pintan SOLO si la política VIVA del restaurante las pide (`politica.pedir_trona` /
 * `pedir_movilidad`, que publica el endpoint `slots`). Ni una línea de esto se hornea en el HTML
 * publicado: por eso el dueño enciende el interruptor en el ERP y aparecen en su web sin volver
 * a publicarla — y quien no las quiera ve exactamente el formulario de siempre.
 */
function pintarExtras(widget, st) {
    const cont = widget.querySelector('.rl-extras');
    if (!cont) return;
    const pol = st.politica || {};
    const items = [];
    if (pol.pedir_trona) items.push(['trona', t('rl_trona')]);
    if (pol.pedir_movilidad) items.push(['movilidad', t('rl_movilidad')]);
    if (!items.length) {
        cont.hidden = true;
        cont.innerHTML = '';
        return;
    }
    // Lo ya marcado sobrevive al repintado (se repinta al cambiar de hora o volver atrás).
    const marcado = {};
    cont.querySelectorAll('.rl-extra-check').forEach((c) => {
        marcado[c.getAttribute('data-extra')] = c.checked;
    });
    cont.hidden = false;
    cont.innerHTML = items.map(([clave, etiqueta]) => `
        <label class="rl-extra">
            <input type="checkbox" class="rl-extra-check" data-extra="${clave}"${marcado[clave] ? ' checked' : ''}>
            <span>${escapeHtml(etiqueta)}</span>
        </label>`).join('');
}

/** Lo que el visitante marcó en las casillas extra, listo para el POST ({trona:'1', …}).
 *  La casilla de consentimiento NO entra aquí (es `.rl-consent-check`, no `.rl-extra-check`):
 *  viaja aparte porque no es una preferencia del comensal sino la base legal del dato. */
function leerExtras(widget) {
    const out = {};
    widget.querySelectorAll('.rl-extra-check').forEach((c) => {
        if (c.checked) out[c.getAttribute('data-extra') || ''] = '1';
    });
    delete out[''];
    return out;
}

// ── Consentimiento del campo de alergias (dato de salud, art. 9 RGPD) ────────
/** El campo con rol `alergias` del widget (puede no existir: el cliente decide sus campos). */
function campoAlergias(widget) {
    return widget.querySelector('[data-field-role="alergias"]');
}

/** ¿Ha escrito el comensal algo en alergias? Espacios NO cuentan (mismo `trim()` que
 *  `leerContacto`, que es lo que de verdad se manda: si ahí no viaja, aquí no se pide). */
function hayAlergias(widget) {
    const campo = campoAlergias(widget);
    return !!(campo && String(campo.value || '').trim());
}

/**
 * La caja de la casilla de consentimiento, creándola si el HTML no la trae.
 *
 * El bloque ya la pinta (`.rl-consent`), pero los sitios PUBLICADOS antes de este cambio no la
 * tienen horneada — y ahí el gate es exactamente igual de obligatorio. Sin este respaldo, un
 * visitante de una web ya publicada escribiría sus alergias y el envío se bloquearía sin casilla
 * que marcar: el peor de los dos fallos posibles.
 */
function cajaConsentimiento(widget) {
    let caja = widget.querySelector('.rl-consent');
    if (!caja) {
        const form = widget.querySelector('.rl-form');
        if (!form) return null;
        caja = document.createElement('div');
        caja.className = 'rl-consent';
        caja.hidden = true;
        const ancla = form.querySelector('.rl-policy') || form.querySelector('.rl-submit');
        if (ancla) form.insertBefore(caja, ancla); else form.appendChild(caja);
    }
    if (!caja.querySelector('.rl-consent-check')) {
        // Una sola vez: repintar el HTML en cada tecla perdería el estado de la casilla.
        caja.innerHTML = `
        <label class="rl-extra rl-consent-label">
            <input type="checkbox" class="rl-consent-check" data-consent="alergias">
            <span>${escapeHtml(t('rl_alergias_ok'))}</span>
        </label>`;
    }
    return caja;
}

/**
 * Enseña u oculta la casilla según haya texto en alergias. Se llama al escribir (delegación de
 * `input`) y al entrar en el paso de datos — el campo puede llegar relleno por autocompletado del
 * navegador, y entonces no hay ningún `input` que escuchar.
 *
 * Al ocultarla se DESMARCA: si el comensal escribe, autoriza y luego borra sus alergias, no puede
 * quedar guardado un consentimiento para un dato que ya no manda.
 */
function pintarConsentimiento(widget) {
    const caja = cajaConsentimiento(widget);
    if (!caja) return;
    const pedir = hayAlergias(widget);
    caja.hidden = !pedir;
    if (!pedir) {
        const check = caja.querySelector('.rl-consent-check');
        if (check) check.checked = false;
    }
}

/** ¿Se puede enviar? Sin alergias no hay nada que consentir; con alergias, la casilla manda. */
function consentimientoOk(widget) {
    if (!hayAlergias(widget)) return true;
    const check = widget.querySelector('.rl-consent-check');
    return !!(check && check.checked);
}

function pintarConfirmacion(widget, st, datos) {
    const dl = widget.querySelector('.rl-done-data');
    if (!dl) return;
    const zona = (st.zonas.find((z) => String(z.id) === String(st.zona)) || {}).name || datos.zona || '';
    // El servidor devuelve la fecha en ISO ('2026-08-05'), que es un dato, no una frase: se pinta
    // con el mismo formato largo y localizado que el resto del asistente.
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(datos.fecha || ''));
    const dia = iso ? fechaLarga(new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])))
        : (datos.fecha || (st.fecha ? fechaLarga(st.fecha) : ''));
    const filas = [
        [t('rl_code'), datos.codigo || ''],
        [t('rl_pick_day'), dia],
        [t('rl_pick_time'), datos.hora || (st.turno ? st.turno.hora : '')],
        [t('rl_zone'), zona],
        [t('rl_people'), String(datos.personas || st.personas)],
    ];
    dl.innerHTML = filas.filter((f) => f[1]).map((f) => `
        <div class="rl-done-row"><dt>${escapeHtml(f[0])}</dt><dd>${escapeHtml(String(f[1]))}</dd></div>`).join('');
}

/** Botones del panel de confirmación: «Anular» solo con llave, «Reservar otra vez» siempre que
 *  haya algo que reiniciar. En el editor no se enseña ninguno (no hay reserva de verdad). */
function pintarAccionesDone(widget, st) {
    const cancelar = widget.querySelector('.rl-cancel');
    const otra = widget.querySelector('.rl-again');
    const hay = !!st.recordada;
    if (cancelar) cancelar.hidden = !(hay && st.recordada.cancel);
    if (otra) otra.hidden = !(hay || st.anulada);
}

/**
 * Arranca el widget en la confirmación de la reserva que este navegador ya hizo.
 * Devuelve true si había una que recordar. Silencioso a propósito (no roba el foco).
 */
function restaurarReserva(widget, st) {
    const g = st.recordada;
    if (!g) return false;
    pintarConfirmacion(widget, st, {
        codigo: g.codigo, fecha: g.fecha, hora: g.hora,
        personas: g.personas, zona: g.zona_nombre || g.zona,
    });
    pintarAccionesDone(widget, st);
    setPaso(widget, st, 'done', { silencioso: true });
    return true;
}

// ── Red ──────────────────────────────────────────────────────────────────────
/** fetch con presupuesto de latencia. Devuelve null ante CUALQUIER problema (nunca lanza). */
async function pedir(url, opciones, ms) {
    const ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    const to = setTimeout(() => { if (ctrl) ctrl.abort(); }, ms);
    try {
        const res = await fetch(url, Object.assign({ signal: ctrl ? ctrl.signal : undefined }, opciones || {}));
        if (!res.ok) return null;
        return await res.json();
    } catch (_) {
        return null;
    } finally {
        clearTimeout(to);
    }
}

/**
 * Carga los huecos del día elegido. Rellena st.turnos/st.alternativas y pinta.
 * SIEMPRE deja algo pintado: a los `SLOTS_PAINT_MS` caen los turnos horneados y se marca
 * `st.degradado` (el envío iría a /site_lead/), pero eso es PROVISIONAL — la petición sigue viva
 * hasta `SLOTS_TIMEOUT_MS` y, si contesta, manda ella y `degradado` vuelve a false.
 */
/**
 * Reconcilia los CHIPS DE SALA con la política viva.
 *
 * Los chips se hornean al publicar el sitio (`data-live-params.zones`), así que un sitio publicado
 * hace meses sigue enseñando las salas de entonces: apagar "Se puede reservar por internet" en el
 * ERP no se notaba hasta volver a publicar, y el visitante podía elegir una sala que el motor iba a
 * rechazar. Aquí manda la lista que llega con cada consulta de huecos.
 *
 * Se REESCRIBE la fila entera (quitar las que ya no están, añadir las nuevas, respetar el orden del
 * servidor) en vez de solo ocultar: una sala dada de alta después de publicar también aparece.
 * Si la sala elegida ha desaparecido, se pasa a la primera que quede — dejar seleccionada una que
 * ya no se ofrece es exactamente el callejón sin salida que esto viene a cerrar.
 */
/**
 * Deja marcado el chip de la sala que hay en `st.zona`. Lo usan el clic del visitante y el
 * deep-link: dos caminos que eligen sala, un solo sitio que lo pinta (y que no se puede olvidar el
 * `aria-checked`, que es lo que oye quien navega con lector de pantalla).
 */
function marcarZona(widget, st) {
    widget.querySelectorAll('.rl-zone').forEach((b) => {
        const on = String(b.getAttribute('data-zone') || '') === String(st.zona || '');
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
}

function sincronizarZonas(widget, st) {
    const vivas = (st.politica && Array.isArray(st.politica.zonas)) ? st.politica.zonas : null;
    if (!vivas) return;
    const cont = widget.querySelector('.rl-zones');
    if (!cont) return;                       // el bloque se insertó sin salas: nada que reconciliar

    // El nombre que manda es el HORNEADO (está traducido al idioma de la página); el del endpoint
    // solo se usa para las salas que el bloque no conocía —dadas de alta después de publicar—, que
    // saldrán en el idioma del ERP hasta la siguiente traducción. El `slug` SIEMPRE es el del
    // servidor: es la clave con la que se pide hueco.
    const horneadas = st.etiquetasHorneadas || {};
    const nuevas = vivas
        .filter((z) => z && z.slug)
        .map((z) => ({
            id: String(z.slug),
            name: horneadas[String(z.slug)] || String(z.nombre || z.slug),
        }));
    const antes = (st.zonas || []).map((z) => String(z.id)).join('|');
    if (antes === nuevas.map((z) => z.id).join('|')) return;      // nada que cambiar

    st.zonas = nuevas;
    if (!nuevas.length) {
        // Ninguna sala reservable: el bloque vuelve a ser "todo el local" y la fila sobra.
        const fila = cont.closest('.rl-row-zones');
        if (fila) fila.hidden = true;
        st.zona = '';
        return;
    }
    const fila = cont.closest('.rl-row-zones');
    if (fila) fila.hidden = false;
    if (!nuevas.some((z) => z.id === st.zona)) { st.zona = nuevas[0].id; }
    cont.innerHTML = nuevas.map((z) => {
        const on = z.id === st.zona;
        return `<button type="button" class="rl-chip rl-zone${on ? ' is-active' : ''}"`
             + ` data-zone="${escapeHtml(z.id)}" role="radio"`
             + ` aria-checked="${on ? 'true' : 'false'}">${escapeHtml(z.name)}</button>`;
    }).join('');
    pintarCalendario(widget, st);            // los días dependen de la sala elegida
}

/**
 * Trae la política (salas, casillas extra, días abiertos) SIN pedir hora todavía.
 *
 * Hace falta porque los chips de sala se ven en el PASO 1 y la política solo llegaba al consultar
 * huecos, que es el paso 3: hasta entonces el visitante veía las salas horneadas al publicar. Se
 * dispara cuando el widget entra en pantalla —no al cargar la página— para no pedir nada por un
 * bloque que nadie llega a ver, y una sola vez por widget.
 */
async function cargarPolitica(widget, st) {
    if (st.politicaPedida || st.modo !== 'live' || !st.restaurantId) return;
    st.politicaPedida = true;
    const qs = new URLSearchParams({
        restaurant_id: st.restaurantId,
        fecha: ymd(new Date()),
        personas: String(st.personas || 2),
        zona: '',                            // sin sala: se quiere la foto COMPLETA del local
    }).toString();
    const data = await pedir(`${basePublica()}${RESERVA_ENDPOINTS.slots}?${qs}`,
                             { headers: { 'X-Requested-With': 'XMLHttpRequest' } }, SLOTS_TIMEOUT_MS);
    // Silencioso a propósito: esto es una MEJORA de lo horneado, no el camino de reservar. Si
    // falla, el visitante sigue viendo lo publicado y el motor rechaza lo que no toque.
    if (!data || !data.politica || typeof data.politica !== 'object') return;
    st.politica = data.politica;
    sincronizarZonas(widget, st);
    pintarExtras(widget, st);
}

async function cargarTurnos(widget, st) {
    const ahora = new Date();
    st.turno = null;
    st.alternativas = [];

    // SECUENCIA — cada carga se numera y solo la ÚLTIMA puede escribir en el estado. Antes daba
    // igual (la respuesta se abandonaba a los 800 ms, así que dos en vuelo era casi imposible);
    // ahora se espera hasta 6 s y dos clics seguidos —martes, miércoles— PUEDEN aterrizar al revés
    // y pintar los huecos del martes bajo la fecha del miércoles. La promesa se resuelve igual
    // (el manejador de alternativas encadena un `.then` sobre ella), solo que sin tocar nada.
    //
    // Y tampoco escribe si el visitante YA ha terminado o está enviando: `.rl-status` vive FUERA de
    // los paneles de paso (siempre visible), así que una respuesta tardía podría estampar "no nos
    // queda mesa" justo debajo de "reserva confirmada". Con 800 ms era imposible llegar a tiempo;
    // con 6 s, no.
    const gen = (st.gen = (st.gen || 0) + 1);
    const vigente = () => st.gen === gen && st.paso !== 'done' && !st.enviando;

    const cerrado = st.fecha ? diaBloqueado(st.cfg, st.fecha, ahora, diasDeSala(st)) : 'pasado';
    if (cerrado === 'cerrado') {
        st.turnos = [];
        st.degradado = false;
        pintarTurnos(widget, st);
        mostrarEstado(widget, 'closed');
        anunciar(widget, t('rl_closed_today'));
        return;
    }

    const local = turnosHorneados(st.cfg, st.fecha, ahora);

    if (st.modo !== 'live' || !st.restaurantId) {
        // Modo "solo avisadme": la política manda y no se consulta a nadie. Sin "quedan X".
        st.turnos = local;
        st.degradado = true;
        pintarTurnos(widget, st);
        mostrarEstado(widget, local.length ? 'ok' : 'full');
        return;
    }

    pintarTurnos(widget, st, { cargando: true });
    const consulta = {
        restaurant_id: st.restaurantId,
        fecha: ymd(st.fecha),
        personas: String(st.personas),
        zona: st.zona || '',
    };
    // `hora` solo viaja cuando de verdad hay una preferencia (deep-link, o el hueco que se acaba de
    // evaporar). Mandarla siempre "por si acaso" —con la primera del turno, p.ej.— fabricaría una
    // preferencia que el visitante no ha expresado y ensuciaría las alternativas; además parte la
    // caché del endpoint por hora (la clave la incluye) y multiplicaría los fallos.
    if (st.horaPref) consulta.hora = st.horaPref;
    const qs = new URLSearchParams(consulta).toString();
    // A los 800 ms se pinta el esqueleto horneado para que la pantalla no se quede colgada, pero la
    // petición SIGUE VIVA: si contesta a los 2 s, manda ella y `degradado` vuelve a false. Marcarlo
    // provisionalmente en true es lo prudente —mientras no haya respuesta no sabemos si hay mesa—;
    // lo que ya no ocurre es que ese true se quede clavado por 100 ms de latencia y se lleve la
    // reserva a /site_lead/. Ver los dos relojes en la cabecera del módulo.
    const reloj = setTimeout(() => {
        if (!vigente()) return;
        st.turnos = local;
        st.degradado = true;
        pintarTurnos(widget, st);
    }, SLOTS_PAINT_MS);

    let data = null;
    try {
        data = await pedir(`${basePublica()}${RESERVA_ENDPOINTS.slots}?${qs}`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }, SLOTS_TIMEOUT_MS);
    } finally {
        clearTimeout(reloj);
    }

    // Llegó tarde y el visitante ya pidió otra cosa: se descarta sin tocar nada (ver SECUENCIA).
    if (!vigente()) return;

    // module_off / respuesta ilegible / timeout → esqueleto horneado + camino de lead.
    if (!data || data.error === 'module_off') {
        st.turnos = local;
        st.degradado = true;
        pintarTurnos(widget, st);
        mostrarEstado(widget, data && data.error === 'module_off' ? 'ok' : 'error');
        return;
    }

    st.degradado = false;
    // POLÍTICA VIVA: de aquí salen las casillas extra (trona / acceso sin escaleras) y la lista de
    // salas. Llega con cada consulta de huecos, así que lo que se toca en el ERP se nota sin
    // republicar la web.
    if (data.politica && typeof data.politica === 'object') {
        st.politica = data.politica;
        sincronizarZonas(widget, st);
        pintarExtras(widget, st);
    }
    const turnos = Array.isArray(data.turnos) ? data.turnos : (Array.isArray(data.slots) ? data.slots : []);
    st.turnos = turnos
        .filter((s) => s && s.hora)
        .map((s) => ({ hora: String(s.hora), estado: estadoDeSlot(s.estado), quedan: (typeof s.quedan === 'number' ? s.quedan : null) , mesas_juntas: (typeof s.mesas_juntas === 'number' ? s.mesas_juntas : null) }));
    st.alternativas = Array.isArray(data.alternativas) ? data.alternativas : [];

    const libres = st.turnos.filter((s) => s.estado === 'ok');
    const espera = st.turnos.filter((s) => s.estado === 'waitlist');
    const estado = data.estado
        || ESTADO_MOTIVO[String(data.motivo || '').toLowerCase()]
        || (libres.length ? 'ok' : (espera.length ? 'waitlist' : (st.turnos.length ? 'full' : 'full')));
    pintarTurnos(widget, st);
    mostrarEstado(widget, ['ok', 'full', 'waitlist', 'closed', 'error'].indexOf(estado) !== -1 ? estado : 'ok');

    // Anuncio de escasez para lector de pantalla: solo con dato REAL (y en MESAS, como el badge).
    const conDato = libres.find((s) => typeof s.quedan === 'number' && s.quedan > 0);
    anunciar(widget, conDato
        ? tvn('rl_left_aria', { n: conDato.quedan, h: conDato.hora })
        : (libres.length ? '' : t('rl_no_slots')));
}

/** Datos de contacto por ROL semántico + los extras por su `name` (nunca por la etiqueta). */
function leerContacto(widget) {
    const out = { roles: {}, etiquetados: [] };
    widget.querySelectorAll('[data-field-role]').forEach((ctrl) => {
        const rol = ctrl.getAttribute('data-field-role') || '';
        const val = (ctrl.value || '').trim();
        if (val) out.roles[rol] = val;
        const campo = ctrl.closest('.rl-field');
        const lab = campo && campo.querySelector('.rl-label');
        out.etiquetados.push([(lab ? lab.textContent.trim() : rol) || rol, ctrl.value || '']);
    });
    return out;
}

/** Validación nativa del navegador sobre los controles del paso de datos. */
function validar(widget) {
    const ctrls = Array.from(widget.querySelectorAll('.rl-field .rl-input'));
    for (let i = 0; i < ctrls.length; i++) {
        if (typeof ctrls[i].checkValidity === 'function' && !ctrls[i].checkValidity()) {
            if (typeof ctrls[i].reportValidity === 'function') ctrls[i].reportValidity();
            return false;
        }
    }
    return true;
}

/** DEGRADACIÓN: el lead de siempre. Contrato de /site_lead/ = {etiqueta: valor} en Lead.data,
 *  así que aquí SÍ se manda por etiqueta (es el contrato existente de ese endpoint, no el nuevo). */
async function enviarComoLead(widget, st, contacto) {
    const fd = new FormData();
    contacto.etiquetados.forEach(([k, v]) => fd.append(k, v));
    // Las casillas extra también viajan por el camino degradado: si el visitante dijo que
    // necesita trona, esa petición no se puede perder porque el motor no contestara.
    const extras = leerExtras(widget);
    if (extras.trona) fd.append(t('rl_trona'), '1');
    if (extras.movilidad) fd.append(t('rl_movilidad'), '1');
    // Por este camino las alergias acaban en un `Lead` y no en `Reservation.extras`, así que la
    // AUTORIZACIÓN se anota igual —con su propia frase como etiqueta, que es el contrato de
    // /site_lead/ ({etiqueta: valor})— o el mismo dato de salud quedaría guardado sin rastro de
    // con qué permiso. `confirmar()` ya ha exigido la casilla antes de llegar aquí.
    if (contacto.roles.alergias) fd.append(t('rl_alergias_ok'), '1');
    fd.append(t('rl_people'), String(st.personas));
    if (st.fecha) fd.append(t('rl_pick_day'), fechaLarga(st.fecha));
    if (st.turno) fd.append(t('rl_pick_time'), st.turno.hora);
    const zona = (st.zonas.find((z) => String(z.id) === String(st.zona)) || {}).name;
    if (zona) fd.append(t('rl_zone'), zona);
    const hp = widget.querySelector('[name="_hp"]');
    fd.append('_hp', hp ? hp.value : '');
    const section = widget.closest('section');
    fd.append('_recipient', (section && section.getAttribute('data-recipient')) || '');
    fd.append('_recipient_type', (section && section.getAttribute('data-recipient-type')) || 'email');
    fd.append('page_url', location.pathname || '');
    const data = await pedir(basePublica() + RESERVA_ENDPOINTS.lead, { method: 'POST', body: fd }, CONFIRM_TIMEOUT_MS);
    return !!(data && data.ok === true);
}

async function confirmar(widget, st) {
    if (st.enviando) return;                       // doble toque en el mismo tick
    // Tope por navegador ANTES de validar: no tiene sentido pedirle que corrija un campo para
    // luego decirle que no. Es fricción honesta y lo dice con todas las letras (llama al local).
    if (topeAlcanzado(st)) { anunciar(widget, t('rl_limit'), 'sacudir'); return; }
    if (!validar(widget)) return;
    // CONSENTIMIENTO del dato de salud, y AQUÍ y no en `validar()`: ese recorre solo los
    // `.rl-field .rl-input`, así que un `required` en la casilla no lo cubriría (quedaría un
    // formulario que el navegador da por válido y el servidor rechaza con un 400).
    // Frena los DOS caminos —reserva real y lead degradado—: en los dos se manda el texto.
    if (!consentimientoOk(widget)) {
        pintarConsentimiento(widget);        // por si aún no estaba pintada (autocompletado)
        anunciar(widget, t('rl_alergias_req'), 'sacudir');
        return;
    }
    const hp = widget.querySelector('[name="_hp"]');
    if (hp && hp.value.trim()) { setPaso(widget, st, 'done'); return; }   // bot: éxito fingido

    st.enviando = true;
    const boton = widget.querySelector('.rl-submit');
    if (boton) boton.setAttribute('disabled', 'disabled');
    const contacto = leerContacto(widget);

    try {
        if (st.modo === 'live' && st.restaurantId && !st.degradado) {
            // Dos intentos COMO MUCHO, y el segundo solo por un motivo: que la idempotencia nos
            // haya devuelto una reserva YA ANULADA (el local la canceló desde el ERP mientras esta
            // clave seguía viva en el navegador). Pintar esa como "confirmada" mandaría a alguien
            // al restaurante sin mesa. Con clave nueva, la petición se atiende como lo que es.
            let data = null;
            let reserva = null;
            for (let intento = 0; intento < 2; intento++) {
                const fd = new FormData();
                fd.append('restaurant_id', st.restaurantId);
                fd.append('fecha', st.fecha ? ymd(st.fecha) : '');
                fd.append('hora', st.turno ? st.turno.hora : '');
                fd.append('personas', String(st.personas));
                fd.append('zona', st.zona || '');
                // Los roles incluyen `alergias`, que aterriza en su COLUMNA (dato de salud: no sale
                // por correo). Antes ese campo viajaba como `notas` y el tratamiento especial no se
                // aplicaba a nada.
                Object.keys(contacto.roles).forEach((rol) => fd.append(rol, contacto.roles[rol]));
                const extras = leerExtras(widget);
                Object.keys(extras).forEach((k) => fd.append(k, extras[k]));
                // La AUTORIZACIÓN viaja junto al dato que autoriza. El servidor la exige igual
                // (400 `consentimiento_requerido`) y la deja registrada con su instante en
                // `Reservation.extras`: el art. 7.1 pide poder DEMOSTRAR el consentimiento, no
                // solo haberlo pedido.
                if (contacto.roles.alergias) fd.append('alergias_consent', '1');
                // La clave se GUARDA ANTES de salir: si la respuesta se pierde y el visitante
                // recarga y reenvía, viaja la misma y `crear_reserva` (paso 1) devuelve la reserva
                // existente en vez de crear una segunda.
                recordarIdem(st.restaurantId, st.idem);
                fd.append('_idem', st.idem);            // el doble toque no reserva dos veces
                fd.append('_hp', hp ? hp.value : '');
                fd.append('page_url', location.pathname || '');
                fd.append('_lang', getRenderLang());
                data = await pedir(basePublica() + RESERVA_ENDPOINTS.confirmar, { method: 'POST', body: fd }, CONFIRM_TIMEOUT_MS);
                // El endpoint devuelve la reserva ANIDADA (`{ok:true, reserva:{codigo,…}}`, ver el
                // contrato en booking_engine.py). Leerla del nivel raíz dejaba la pantalla de
                // confirmación SIN código — justo el dato que el comensal enseña en la puerta.
                // Se acepta también el objeto plano por si un servidor viejo respondiera así.
                reserva = (data && data.ok === true) ? (data.reserva || data) : null;
                if (!reserva || String(reserva.estado || '') !== 'cancelada') break;
                lsBorrar(LS_IDEM + st.restaurantId);
                st.idem = nuevoIdem();
            }
            // Cinturón: si tras el reintento SIGUE llegando una reserva anulada, no se pinta como
            // confirmada — se cae al camino de lead, que promete revisarla a mano y no una mesa.
            if (reserva && String(reserva.estado || '') !== 'cancelada') {
                // Se recuerda ANTES de pintar: si el visitante recarga justo ahora, ya está
                // guardada (con su clave y su llave de anulación) y no reserva dos veces.
                st.recordada = guardarReserva(st.restaurantId, st, reserva);
                st.anulada = false;
                // Solo cuenta la reserva NUEVA. Si el servidor respondió `creada:false`, esto es un
                // reenvío de la MISMA clave (recargó y volvió a enviar, o se perdió la respuesta):
                // la reserva ya estaba hecha y no debe gastar una segunda plaza del tope. Sin esto,
                // una sola reserva real podía agotar el cupo de dos. Un servidor anterior a este
                // campo no manda `creada` → se cuenta, que es el comportamiento de siempre.
                if (!data || data.creada !== false) anotarReserva(st.restaurantId);
                pintarConfirmacion(widget, st, reserva);
                pintarAccionesDone(widget, st);
                setPaso(widget, st, 'done');
                anunciar(widget, t('rl_confirmed'));
                return;
            }
            // El hueco se evaporó mientras rellenaba: volver a la hora con las alternativas.
            if (data && data.ok === false && data.error !== 'module_off') {
                st.alternativas = Array.isArray(data.alternativas) ? data.alternativas : st.alternativas;
                // La hora que QUERÍA se guarda como preferencia antes de soltarla: la recarga de
                // abajo se la pide al motor y vuelve con alternativas alrededor de ESA hora
                // ("misma noche, media hora antes") en vez de con las de la consulta anterior.
                st.horaPref = (st.turno && st.turno.hora) || st.horaPref;
                st.turno = null;
                // Y se vuelven a pedir los huecos: `confirmar` no devuelve agenda, así que sin esto
                // se repintaba la lista VIEJA — con el hueco recién perdido todavía pintado como
                // libre. El visitante lo volvía a pulsar y se lo volvían a rechazar.
                st.recargarTrasEnviar = true;
                pintarTurnos(widget, st);
                mostrarEstado(widget, 'full');
                setPaso(widget, st, 'time');
                anunciar(widget, t('rl_no_slots'));
                return;
            }
            // module_off / timeout / 5xx → NO se pierde la reserva: cae al lead de siempre.
        }
        await enviarComoLead(widget, st, contacto);
        pintarConfirmacion(widget, st, {});
        setPaso(widget, st, 'done');
        anunciar(widget, t('rl_confirmed'));
    } finally {
        st.enviando = false;
        if (boton) boton.removeAttribute('disabled');
        // AQUÍ y no dentro del `try`: la recarga pasa por `cargarTurnos`, que se niega a escribir
        // mientras `st.enviando` siga en true (es una de las guardas que impiden que una respuesta
        // tardía pinte sobre una confirmación). Lanzarla desde el `finally`, ya con la bandera
        // bajada, es lo que la hace determinista.
        if (st.recargarTrasEnviar) {
            st.recargarTrasEnviar = false;
            cargarTurnos(widget, st);
        }
    }
}

// ── Sobre una reserva YA hecha ───────────────────────────────────────────────
/**
 * «Reservar otra vez»: olvida la reserva recordada y empieza de cero con una clave NUEVA.
 *
 * Existe porque el recuerdo no puede ser una jaula: una familia puede querer dos mesas el mismo
 * fin de semana y nadie tiene derecho a impedírselo desde el navegador. La clave nueva es lo que
 * hace que el servidor la trate como lo que es —otra reserva— en vez de devolver la anterior.
 */
function reiniciar(widget, st) {
    lsBorrar(LS_RESERVA + st.restaurantId);
    lsBorrar(LS_IDEM + st.restaurantId);     // la clave vieja no puede seguir viva
    st.recordada = null;
    st.anulada = false;
    st.idem = nuevoIdem();
    st.fecha = null;
    st.turno = null;
    st.turnos = [];
    st.alternativas = [];
    st.mes = startOfDay(new Date());
    pintarPersonas(widget, st);
    pintarCalendario(widget, st);
    pintarAccionesDone(widget, st);
    setPaso(widget, st, 'people');
    mostrarEstado(widget, '');
    // Resaltado, no sacudido: aquí el visitante acaba de llegar, no ha intentado nada todavía.
    anunciar(widget, topeAlcanzado(st) ? t('rl_limit') : '', 'alerta');
}

/**
 * «Anular»: la MISMA puerta firmada que el enlace del correo (`/site_reserva/cancelar/`), con el
 * token que el endpoint de confirmación entregó a quien reservó. Aquí NO hay lógica de
 * cancelación: liberar las mesas y descontar el contador es cosa de `booking_engine`, y decidir
 * si esta persona puede pedirlo, del token. Este botón solo lo pide.
 */
async function anular(widget, st, boton) {
    const g = st.recordada;
    if (!g || !g.cancel || st.enviando) return;
    st.enviando = true;
    if (boton) boton.setAttribute('disabled', 'disabled');
    try {
        const fd = new FormData();
        fd.append('t', g.cancel);
        const data = await pedir(basePublica() + RESERVA_ENDPOINTS.cancelar, {
            method: 'POST', body: fd, headers: { 'X-Requested-With': 'XMLHttpRequest' },
        }, CONFIRM_TIMEOUT_MS);
        if (!data || data.ok !== true) {
            anunciar(widget, t('rl_error'));
            return;
        }
        lsBorrar(LS_RESERVA + st.restaurantId);
        // La clave en vuelo TAMBIÉN se olvida: si sobreviviera, el siguiente envío de este
        // navegador la reutilizaría y el servidor devolvería —por idempotencia— la reserva que
        // acabamos de anular, pintándola como confirmada.
        lsBorrar(LS_IDEM + st.restaurantId);
        descontarReserva(st.restaurantId);   // quien anula no ha gastado su cupo del navegador
        st.recordada = null;
        st.anulada = true;
        const titulo = widget.querySelector('.rl-done-title');
        if (titulo) titulo.textContent = t('rl_cancelled');
        const dl = widget.querySelector('.rl-done-data');
        if (dl) dl.innerHTML = '';
        pintarAccionesDone(widget, st);
        anunciar(widget, t('rl_cancelled'));
    } finally {
        st.enviando = false;
        if (boton) boton.removeAttribute('disabled');
    }
}

// ── Hidratación ──────────────────────────────────────────────────────────────
/**
 * Pinta los widgets que aún no lo estén. Idempotente: la marca va en el DOM (`data-rl-ready`),
 * así que un re-render del editor (elemento nuevo, sin marca) se vuelve a pintar solo.
 */
export function hydrateReservaLive() {
    if (typeof document === 'undefined') return;
    document.querySelectorAll('.rl-widget:not([data-rl-ready])').forEach((w) => {
        w.setAttribute('data-rl-ready', '1');
        const st = estadoDe(w);
        pintarPersonas(w, st);
        pintarCalendario(w, st);
        // Si este navegador ya reservó aquí, se arranca en SU confirmación en vez de en el paso 1:
        // sin esto, el visitante volvía a rellenar el formulario y reservaba dos veces sin querer.
        // El deep-link va DESPUÉS a propósito: quien ya tiene mesa no puede ser expulsado de su
        // confirmación por un enlace: para eso está «Reservar otra vez».
        if (restaurarReserva(w, st)) return;
        setPaso(w, st, 'people');
        mostrarEstado(w, '');
        aplicarDeepLink(w, st);
        vigilarParaPolitica(w, st);
    });
}

/**
 * Arranca el asistente con lo que traiga la URL (`?d=&p=&z=&h=`).
 *
 * Es el otro extremo del `ReserveAction` que el SEO publica a Google (ver `utils/deep-link.js`) y
 * también sirve para los enlaces que el propio local reparte ("reserva para el sábado"). Cuánto se
 * adelanta el asistente depende de cuánto traiga el enlace, y ni un paso más: con día válido se
 * salta a elegir hora —que es donde hay que consultar al servidor—, con solo el grupo se abre el
 * calendario, y sin nada se queda como estaba.
 *
 * En el EDITOR no corre: allí la URL es la del builder y sus parámetros no son de nadie.
 *
 * El día NO se valida aquí contra el calendario: se pregunta a `diaBloqueado`, el mismo espejo de
 * las reglas del motor que usa el calendario para tachar días. Un enlace viejo (el sábado que ya
 * pasó, o uno fuera de antelación) deja el día sin poner y el visitante elige — nunca se arranca en
 * un día que el servidor va a rechazar.
 */
function aplicarDeepLink(widget, st) {
    if (esEditor() || typeof location === 'undefined') return;
    let d = {};
    try {
        d = leerDeepLink(location.search, {
            paxMin: st.cfg.party_min,
            paxMax: st.cfg.party_max,
            zonas: (st.zonas || []).map((z) => String(z.id)),
        });
    } catch (_) {
        return;                              // un enlace raro no puede dejar el bloque sin arrancar
    }
    if (!d.personas && !d.fecha && !d.zona && !d.hora) return;

    if (d.zona) { st.zona = d.zona; marcarZona(widget, st); }
    if (d.personas) { st.personas = d.personas; pintarPersonas(widget, st); }
    // La hora se guarda como PREFERENCIA aunque no haya día: si el visitante elige uno luego, las
    // alternativas ya saldrán ordenadas alrededor de la hora que pidió el enlace.
    if (d.hora) st.horaPref = d.hora;

    if (d.fecha) {
        const p = d.fecha.split('-').map(Number);
        const fecha = new Date(p[0], p[1] - 1, p[2]);
        if (!diaBloqueado(st.cfg, fecha, new Date(), diasDeSala(st))) {
            st.fecha = fecha;
            st.mes = new Date(p[0], p[1] - 1, 1);
        }
    }
    pintarCalendario(widget, st);

    if (st.fecha) {
        setPaso(widget, st, 'time');
        cargarTurnos(widget, st).then(() => {
            // Si el enlace traía hora Y sigue libre, se deja elegida: el visitante llega
            // directamente al paso de sus datos. Si ya no lo está, se queda en la lista de horas
            // con las alternativas que el motor ha calculado ALREDEDOR de esa hora — que es
            // justamente lo que `horaPref` ha ido a pedir.
            if (!st.horaPref) return;
            const libre = (st.turnos || []).find((s) => s.hora === st.horaPref && s.estado === 'ok');
            if (!libre) return;
            st.turno = libre;
            pintarTurnos(widget, st);
            pintarResumen(widget, st);
            setPaso(widget, st, 'details');
        });
    } else if (d.personas || d.zona) {
        setPaso(widget, st, 'day');
    }
}

/**
 * Pide la política cuando el widget entra en pantalla. En el EDITOR no: allí el bloque se pinta y
 * repinta constantemente y no hay sitio publicado que corregir.
 */
function vigilarParaPolitica(widget, st) {
    if (esEditor()) return;
    // Si ya está a la vista se pide y punto, sin delegar en el observer. Un `IntersectionObserver`
    // que no llega a disparar —pestaña en segundo plano, incrustaciones, entornos sin viewport—
    // dejaría las salas horneadas para siempre, que es justo el fallo que esto viene a arreglar.
    // El observer se queda solo para el caso que de verdad lo necesita: el bloque que está más
    // abajo y al que quizá nadie llegue.
    const alcance = (window.innerHeight || 0) + 200;
    const r = widget.getBoundingClientRect();
    if (r.bottom > -200 && r.top < alcance) { cargarPolitica(widget, st); return; }
    if (typeof IntersectionObserver !== 'function') { cargarPolitica(widget, st); return; }
    const obs = new IntersectionObserver((entradas) => {
        entradas.forEach((e) => {
            if (!e.isIntersecting) return;
            obs.disconnect();
            cargarPolitica(widget, st);
        });
    }, { rootMargin: '200px' });             // un poco antes de verse: da tiempo a que llegue
    obs.observe(widget);
}

function widgetDe(el) {
    return el && el.closest ? el.closest('.rl-widget') : null;
}

/** Instala la delegación del asistente. Idempotente (guard global), corre en editor y publicado. */
export function initReservaLive() {
    if (typeof window === 'undefined' || window.__n3iReservaLive) return;
    window.__n3iReservaLive = true;

    document.addEventListener('click', (e) => {
        const w = widgetDe(e.target);
        if (!w) return;
        const st = estadoDe(w);

        // Nº de comensales
        const persona = e.target.closest('.rl-person');
        if (persona && w.contains(persona)) {
            const v = persona.getAttribute('data-people');
            if (v === 'more') {
                // "10+" NO es un número de comensales: es "somos más de los que caben aquí".
                // Antes fijaba `party_max` en silencio, así que un grupo de veinte se quedaba
                // pidiendo mesa para doce sin que nadie le dijera nada — y ese es el cliente que
                // más le interesa al restaurante. Ahora se le manda al camino de contacto, que es
                // el mismo que atiende cuando el motor no responde: deja sus datos y el local le
                // llama. El tope se sigue poniendo para que el formulario tenga un número
                // coherente si acaba enviándolo.
                st.personas = Number(st.cfg.party_max) || 12;
                st.degradado = true;
                st.grupoGrande = true;
                // Se SUELTA lo elegido antes. Al saltar al formulario nos saltamos el día y la
                // hora, así que lo que hubiera quedado ahí está calculado para OTRO número de
                // comensales: pulsar "Volver" enseñaba huecos de un grupo distinto ("juntamos 3
                // mesas, queda 1 hueco") como si valieran para este. Prometer disponibilidad que no
                // se ha comprobado es peor que no enseñar nada.
                st.fecha = null;
                st.turno = null;
                st.turnos = [];
                st.alternativas = [];
                pintarPersonas(w, st);
                setPaso(w, st, 'details');
                // SIN copy de estado. Se probó con el de 'error' —el del modo degradado— y decía
                // "No hemos podido comprobar la disponibilidad": eso echa la culpa a una avería que
                // no existe. Aquí no ha fallado nada; simplemente el grupo no cabe por la web. Lo
                // explica el aviso, y en sus palabras.
                mostrarEstado(w, '');
                anunciar(w, t('rl_grupo_grande'), 'alerta');
                return;
            }
            st.personas = Number(v) || st.personas;
            // Elegir un número normal deshace el modo "grupo grande": el aviso de que les llamamos
            // seguía en pantalla mientras el visitante ya reservaba con normalidad, y no se iba
            // hasta elegir día. Un mensaje que sobrevive a la decisión que lo provocó se lee como
            // que el sistema no se ha enterado.
            if (st.grupoGrande) {
                st.grupoGrande = false;
                st.degradado = false;
                anunciar(w, '');
                mostrarEstado(w, '');
            }
            pintarPersonas(w, st);
            return;
        }

        // Zona
        const zona = e.target.closest('.rl-zone');
        if (zona && w.contains(zona)) {
            st.zona = zona.getAttribute('data-zone') || '';
            marcarZona(w, st);
            // Cada sala tiene sus días de servicio, así que el calendario cambia con ella: si el
            // reservado solo abre los domingos, el resto de la semana se tacha AQUÍ y no cuando el
            // visitante ya ha pulsado un jueves. Y si el día que tenía elegido deja de valer para
            // la sala nueva, se suelta: seguir adelante con él llevaría a un "no hay mesa" que en
            // realidad es "ese día esta sala no abre".
            const dias = diasDeSala(st);
            if (st.fecha && Array.isArray(dias) && dias.length
                && dias.map(Number).indexOf(st.fecha.getDay()) === -1) {
                st.fecha = null;
                st.turno = null;
            }
            pintarCalendario(w, st);
            return;
        }

        // Navegación de mes
        const nav = e.target.closest('.rl-cal-prev, .rl-cal-next');
        if (nav && w.contains(nav)) {
            const delta = nav.classList.contains('rl-cal-prev') ? -1 : 1;
            st.mes = new Date(st.mes.getFullYear(), st.mes.getMonth() + delta, 1);
            pintarCalendario(w, st);
            return;
        }

        // Día
        const dia = e.target.closest('.rl-day');
        if (dia && w.contains(dia) && !dia.disabled && dia.getAttribute('data-day')) {
            const partes = dia.getAttribute('data-day').split('-').map(Number);
            st.fecha = new Date(partes[0], partes[1] - 1, partes[2]);
            pintarCalendario(w, st);
            setPaso(w, st, 'time');
            cargarTurnos(w, st);
            return;
        }

        // Hora
        const slot = e.target.closest('.rl-slot');
        if (slot && w.contains(slot) && !slot.disabled) {
            const hora = slot.getAttribute('data-slot');
            st.turno = st.turnos.find((s) => s.hora === hora) || { hora, estado: 'ok', quedan: null };
            pintarTurnos(w, st);
            pintarResumen(w, st);
            setPaso(w, st, 'details');
            return;
        }

        // Alternativa ("no hay mesa" nunca es un callejón sin salida)
        const alt = e.target.closest('.rl-alt');
        if (alt && w.contains(alt)) {
            const a = st.alternativas[Number(alt.getAttribute('data-alt'))] || {};
            if (a.zona) st.zona = String(a.zona);
            if (a.fecha) {
                const p = String(a.fecha).split('-').map(Number);
                if (p.length === 3) { st.fecha = new Date(p[0], p[1] - 1, p[2]); st.mes = new Date(p[0], p[1] - 1, 1); }
            }
            pintarCalendario(w, st);
            cargarTurnos(w, st).then(() => {
                if (!a.hora) return;
                st.turno = st.turnos.find((s) => s.hora === a.hora) || null;
                pintarTurnos(w, st);
            });
            return;
        }

        // Continuar / Volver
        const next = e.target.closest('.rl-next');
        if (next && w.contains(next)) {
            if (st.paso === 'people') { pintarCalendario(w, st); setPaso(w, st, 'day'); }
            else if (st.paso === 'day' && st.fecha) { setPaso(w, st, 'time'); cargarTurnos(w, st); }
            // Continuar sin haber elegido día no puede ser un no-op mudo: quien navega con
            // lector de pantalla no vería NADA y creería que el botón está roto.
            else if (st.paso === 'day') anunciar(w, t('rl_pick_day'));
            return;
        }
        const back = e.target.closest('.rl-back');
        if (back && w.contains(back)) {
            // Volver desde el formulario en modo "grupo grande" lleva a los COMENSALES, no al paso
            // anterior de la fila: a ese grupo no se le llegó a pedir día ni hora, así que pasar por
            // esos pasos sería devolverle a una pantalla que nunca vio y que además está vacía.
            if (st.grupoGrande && st.paso === 'details') {
                setPaso(w, st, 'people');
                return;
            }
            const i = Math.max(0, STEPS.indexOf(st.paso) - 1);
            setPaso(w, st, STEPS[i]);
            return;
        }

        // Reservar otra vez (limpia el recuerdo y estrena clave de idempotencia)
        const otra = e.target.closest('.rl-again');
        if (otra && w.contains(otra)) {
            reiniciar(w, st);
            return;
        }

        // Anular la reserva ya hecha (misma puerta firmada que el enlace del correo)
        const anularBtn = e.target.closest('.rl-cancel');
        if (anularBtn && w.contains(anularBtn)) {
            if (esEditor()) return;                 // en el canvas del editor no hay reserva real
            anular(w, st, anularBtn);
            return;
        }

        // Confirmar (el botón es type=submit dentro de .rl-form → normalmente llega por 'submit';
        // esta rama cubre el caso de que el botón quede fuera del form en un HTML antiguo).
        const submit = e.target.closest('.rl-submit');
        if (submit && w.contains(submit) && submit.type !== 'submit') {
            if (esEditor()) return;                 // ENVÍO INERTE en el canvas del editor
            confirmar(w, st);
        }
    });

    // Envío: espejo exacto de form-submit.js (preventDefault siempre; inerte en editor-active).
    document.addEventListener('submit', (e) => {
        const form = e.target.closest && e.target.closest('.rl-form');
        if (!form) return;
        e.preventDefault();
        if (esEditor()) return;
        const w = widgetDe(form);
        if (w) confirmar(w, estadoDe(w));
    });

    // Alergias: la casilla de consentimiento aparece EN CUANTO se escribe algo, y desaparece si se
    // borra. Listener propio y no dentro de `cargarTurnos` (que es donde se pintan trona/movilidad)
    // porque esto no depende de la política del restaurante sino de lo que teclea el visitante, y
    // ocurre bastante después: la política llega al elegir día, el texto se escribe al final.
    document.addEventListener('input', (e) => {
        const campo = e.target.closest && e.target.closest('[data-field-role="alergias"]');
        if (!campo) return;
        const w = widgetDe(campo);
        if (w) pintarConsentimiento(w);
    });

    // Input nativo de fecha (escape hatch accesible): alimenta el MISMO estado.
    document.addEventListener('change', (e) => {
        const nativo = e.target.closest && e.target.closest('.rl-date-native');
        if (!nativo) return;
        const w = widgetDe(nativo);
        if (!w) return;
        const st = estadoDe(w);
        const p = String(nativo.value || '').split('-').map(Number);
        if (p.length !== 3 || !p[0]) return;
        st.fecha = new Date(p[0], p[1] - 1, p[2]);
        st.mes = new Date(p[0], p[1] - 1, 1);
        pintarCalendario(w, st);
        setPaso(w, st, 'time');
        cargarTurnos(w, st);
    });

    // Teclado en el calendario: flechas ±1 día / ±1 semana, Inicio-Fin a los extremos visibles.
    document.addEventListener('keydown', (e) => {
        const dia = e.target.closest && e.target.closest('.rl-day');
        if (!dia) return;
        const salto = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];
        if (!salto && e.key !== 'Home' && e.key !== 'End') return;
        const w = widgetDe(dia);
        if (!w) return;
        const dias = Array.from(w.querySelectorAll('.rl-day:not(.rl-day-void)'));
        const i = dias.indexOf(dia);
        let destino = e.key === 'Home' ? 0 : (e.key === 'End' ? dias.length - 1 : i + salto);
        destino = Math.max(0, Math.min(dias.length - 1, destino));
        if (destino === i) return;
        e.preventDefault();
        dias[destino].focus();
    });

    hydrateReservaLive();
}
